"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.propTypes = exports.defaultProps = exports.default = exports.ProductCardCollection = void 0;
require("./ProductCardCollection.css");
var _classnames = _interopRequireDefault(require("classnames"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _react = _interopRequireDefault(require("react"));
var _conditionalAttributes = require("../../util/conditionalAttributes/conditionalAttributes");
var _renderObject = require("../../util/renderObject/renderObject");
var _LoaderGhostCard = _interopRequireDefault(require("../LoaderGhostCard/LoaderGhostCard"));
var _Text = _interopRequireDefault(require("../Text/Text"));
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
/**
 * ProductCardCollection component
 * @method
 * @param {object} props - React properties passed from composition
 * @returns ProductCardCollection
 */

const ProductCardCollection = props => {
  const {
    /* actions */
    externalActionElement,
    // the fully clickable link action
    fullyClickableParent,
    // boolean if card can be fully clickable

    /* external images */
    externalImageElements,
    // array of UltaAssets or external image elements with mapped keys

    /* title/subtitle */
    title,
    titleTag,
    subtitle,
    subtitleTag,
    /* color, theme and elevation */
    backgroundColor = defaultProps.backgroundColor,
    elevation = defaultProps.elevation,
    theme = defaultProps.theme,
    /* loading state */
    isLoading = defaultProps.isLoading,
    loaderHeightSm,
    loaderHeightMd,
    loaderHeightLg,
    loaderHeightXl
  } = props;
  const hasImages = externalImageElements && externalImageElements.length >= 1;
  const singleImage = externalImageElements && externalImageElements.length === 1;
  const TagName = singleImage ? 'div' : 'ul';
  const componentClassName = (0, _classnames.default)(`pal-c-ProductCardCollection`, {
    'pal-c-ProductCardCollection--backgroundColor': backgroundColor,
    'pal-c-ProductCardCollection__hasLink': fullyClickableParent
  });
  const componentContainer = (0, _classnames.default)(`pal-c-ProductCardCollection__container`, {
    [`pal-c-ProductCardCollection__elevation--${elevation}`]: elevation
  });
  const containerStyle = {
    '--backgroundColor': `var( --pal-colors-${theme}-${backgroundColor} )`,
    ...(0, _conditionalAttributes.conditionalAttributes)(isLoading ? {
      '--pal-loaderghost-card-height-xl': loaderHeightXl,
      '--pal-loaderghost-card-height-lg': loaderHeightLg,
      '--pal-loaderghost-card-height-md': loaderHeightMd,
      '--pal-loaderghost-card-height-sm': loaderHeightSm
    } : null)
  };
  const productCardCollectionContent = () => {
    if (isLoading) {
      return /*#__PURE__*/_react.default.createElement(_LoaderGhostCard.default, {
        className: "pal-c-ProductCardCollection__loader",
        varirant: "default"
      });
    } else {
      return /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, /*#__PURE__*/_react.default.createElement("div", {
        className: componentContainer
      }, fullyClickableParent && /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, externalActionElement && /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, (0, _renderObject.renderObject)(externalActionElement))), /*#__PURE__*/_react.default.createElement("div", {
        className: "pal-c-ProductCardCollection__titleAndSubtitle"
      }, /*#__PURE__*/_react.default.createElement(_Text.default, {
        htmlTag: titleTag,
        textStyle: 'title-sm',
        textColor: 'content-default'
      }, title), /*#__PURE__*/_react.default.createElement(_Text.default, {
        htmlTag: subtitleTag,
        textStyle: 'body-sm',
        textColor: 'xsubtle'
      }, subtitle)), hasImages && /*#__PURE__*/_react.default.createElement(TagName, {
        className: "pal-c-ProductCardCollection__images"
      }, externalImageElements && externalImageElements)));
    }
  };
  return /*#__PURE__*/_react.default.createElement("div", {
    className: componentClassName,
    style: containerStyle
  }, productCardCollectionContent());
};
/**
 * Property type definitions
 * @typedef ProductCardCollectionProps
 * @type {object}
 * @property {string} backgroundColor - string value that sets the background color of the card.
 * @property {string} elevation - string value that sets the elevation.
 * @property {object} externalActionElement - click action for the card.
 * @property {Array} externalImageElements - array of UltaAssets or external image elements.
 * @property {boolean} fullyClickableParent - boolean value to set if the parent is fully clickable.
 * @property {string} subtitle - string value that sets the subtitle text.
 * @property {string} subtitleTag - string value that tag of subtitle(e.g: p, span,etc.)
 * @property {string} theme - sets the theme at the component level.
 * @property {string} title - string value that sets the title text.
 * @property {string} titleTag - string value that sets the tag of title.
 * @property {boolean} isLoading - Sets the loading state of the product card collection
 * @property {string} loaderHeightSm - Accepts the height of the loader for small viewport( e.g. "32.2rem" )
 * @property {string} loaderHeightMd - Accepts the height of the loader for medium viewport( e.g. "32.2rem" )
 * @property {string} loaderHeightLg - Accepts the height of the loader for large viewport( e.g. "32.2rem" )
 * @property {string} loaderHeightXl - Accepts the height of the loader for xLarge viewport( e.g. "32.2rem" )
 */
exports.ProductCardCollection = ProductCardCollection;
const propTypes = exports.propTypes = {
  /**
  * Sets the background color for ProductCardCollection
  */
  backgroundColor: _propTypes.default.string,
  /**
   * String to set the elevation of the card.
   */
  elevation: _propTypes.default.string,
  /**
  * Accepts click action for the card.
  */
  externalActionElement: _propTypes.default.object,
  /**
  * Accepts array of UltaAssets or external image elements
  */
  externalImageElements: _propTypes.default.array,
  /**
   * Boolean value to show that the card is fully clickable
  */
  fullyClickableParent: _propTypes.default.bool,
  /**
   * Sets the subtitle text of the Card
   */
  subtitle: _propTypes.default.string,
  /**
   * Sets the html tag for subtitle
   */
  subtitleTag: _propTypes.default.string,
  /**
   * Sets the theme at component level
  */
  theme: _propTypes.default.string,
  /**
   * Sets the title text of the ProductCardCollection
  */
  title: _propTypes.default.string,
  /**
   * Sets the html tag for title
  */
  titleTag: _propTypes.default.string,
  /**
   * Sets the loading state of the product card collection
   */
  isLoading: _propTypes.default.bool,
  /**
   * Accepts the height of the loader for small viewport.
   * Expect a CSS length string (for example: **"32.2rem"**)
   */
  loaderHeightSm: _propTypes.default.string,
  /**
   * Accepts the height of the loader for medium viewport.
   * Expect a CSS length string (for example: **"32.2rem"**)
   */
  loaderHeightMd: _propTypes.default.string,
  /**
   * Accepts the height of the loader for large viewport.
   * Expect a CSS length string (for example: **"32.2rem"**)
   */
  loaderHeightLg: _propTypes.default.string,
  /**
   * Accepts the height of the loader for Xlarge viewport.
   * Expect a CSS length string (for example: **"32.2rem"**)
   */
  loaderHeightXl: _propTypes.default.string
};

/**
 * Default values for passed properties
 * @type {object}
 * @property {string} backgroundColor - backgroundColor: 'background-default' - sets the default background color
 * @property {string} elevation - elevation: 'medium' - sets the default elevation
 * @property {string} theme - theme: 'default' - sets the default theme
 * @property {boolean} isLoading - isLoading: false - sets the default loading state of the card
 */

const defaultProps = exports.defaultProps = {
  backgroundColor: 'background-default',
  elevation: 'medium',
  theme: 'default',
  isLoading: false
};
ProductCardCollection.propTypes = propTypes;
var _default = exports.default = ProductCardCollection;